import React from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import AvatarSkeleton from '@atlaskit/avatar/Skeleton';
import { IdentityUserAri } from '@atlassian/ari/identity/user';
import { AvatarRelay } from '@atlassian/avatar-relay';

import type { TelepointerAvatarQuery } from './__generated__/TelepointerAvatarQuery.graphql';

export interface TelepointerAvatarProps {
	/**
	 * Atlassian identity account ARI (e.g. "ari:cloud:identity::user/712020:...").
	 * Used to look up the agent's avatar via user(accountId) + AvatarRelay.
	 */
	agentIdentityAccountId?: string | null;
}

const TelepointerAvatarInner = ({ accountId }: { accountId: string }): React.JSX.Element | null => {
	const data = useLazyLoadQuery<TelepointerAvatarQuery>(
		graphql`
			query TelepointerAvatarQuery($accountId: ID!) {
				user(accountId: $accountId) {
					...avatarRelay_user
				}
			}
		`,
		{ accountId },
		{ fetchPolicy: 'store-or-network' },
	);

	return <AvatarRelay userFragment={data.user ?? null} size="xxsmall" />;
};

export const TelepointerAvatar = ({
	agentIdentityAccountId,
}: TelepointerAvatarProps): React.JSX.Element | null => {
	if (!agentIdentityAccountId) {
		return null;
	}
	let accountId: string;
	try {
		accountId = IdentityUserAri.parse(agentIdentityAccountId).userId;
	} catch {
		return null;
	}

	return (
		<React.Suspense fallback={<AvatarSkeleton size="xxsmall" appearance="hexagon" />}>
			<TelepointerAvatarInner accountId={accountId} />
		</React.Suspense>
	);
};
